//import external from "./../../externalModules.js";
import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";

// State
import { getToolState } from "component/toolManager/toolState.js";
import toolStyle from "component/toolManager/toolStyle.js";
import toolColors from "component/toolManager/toolColors.js";
import textStyle from "component/toolManager/textStyle.js";

// Drawing
import {
  getNewContext,
  draw,
  drawHandles,
  drawEllipse,
  drawLinkedTextBoxStyle,
  setShadow,
} from "./../../draw/index.js";

// Util
import calculateSUV from "component/util/calculateSUV.js";
import numbersWithCommas from "component/util/numbersWithCommas.js";
import throttle from "component/util/throttle.js";
import { ellipticalRoiCursor } from "../cursors/index.js";

import math from "core/math";
import getPixels from "component/molecules/viewport/core/lookupTable/getPixels.js";
import Helper from "helper";
/**
 * @public
 * @class EllipseRoiTool
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing rectangular regions of interest, and measuring
 * the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class EllipseRoiTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "ellipse",
      supportedInteractionTypes: ["Mouse", "Touch"],
      configuration: { showMinMax: true },
      svgCursor: ellipticalRoiCursor,
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);
    this.initialConfiguration = initialConfiguration;
  }

  createNewMeasurement(eventData) {
    const { image } = eventData;
    //if (!image || !image.pixelSpacing) {
    //if (!image || !image.pixelSpacing) {
    // 1. 영상 데이터가 없다면, 그리기를 생성하지 않음
    if (!image) {
      return;
    }

    // 2. US 영상일 경우, Physical Delta/Physical Direction Unit
    // 이외 영상일 경우, PixelSpacing이 없다면 그리기를 생성하지 않음
    const modality = image.modality;
    if (
      Helper.Validator.isDefined(modality) === false ||
      Helper.Validator.isString(modality) === false
    ) {
      return;
    }

    if (modality.toLowerCase() === "us") {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === -1 || units.y === -1) {
        return;
      }

      if (
        image.hasPhysicalDelta === false &&
        image.isExistPixelSpacing === false
      ) {
        return;
      }
    } else {
      if (
        image.isExistPixelSpacing === false &&
        image.usePixelSpacingApproximateValue === false
      ) {
        return;
      }
    }
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      //   logger.error(
      //     `required eventData not supplied to tool ${
      //       this.name
      //     }'s createNewMeasurement`
      //   );

      return;
    }

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          // left top
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          // right bottom
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        lb: {
          // left bottom
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        rt: {
          // right top
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        initialRotation: eventData.viewport.rotation,
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
      willbeRemove: true,
      moveCount: 0,
    };
  }

  pointNearTool(element, data, coords, interactionType) {
    const hasStartAndEndHandles =
      data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      //   logger.warn(
      //     `invalid parameters supplied to tool ${this.name}'s pointNearTool`
      //   );
    }

    if (!validParameters || data.visible === false) {
      return false;
    }

    const distance = interactionType === "mouse" ? 15 : 25;
    const startCanvas = math.coordinate.pixelToCanvas(
      element,
      data.handles.start
    );
    const endCanvas = math.coordinate.pixelToCanvas(element, data.handles.end);

    const rect = {
      left: Math.min(startCanvas.x, endCanvas.x),
      top: Math.min(startCanvas.y, endCanvas.y),
      width: Math.abs(startCanvas.x - endCanvas.x),
      height: Math.abs(startCanvas.y - endCanvas.y),
    };

    const distanceToPt = math.MathRect.distanceToPoint(rect, coords);

    return distanceToPt < distance;
  }

  renderToolData(evt) {
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const eventData = evt.detail;
    let { image, element } = eventData;
    //if (!image || !image.pixelSpacing) {
    if (!image) {
      return;
    }
    const modality = image.modality;

    if (!image.pixelSpacing && modality.toLowerCase() != "us") {
      return;
    }

    let hasUnit = true;
    if (modality.toLowerCase() === "us") {
      const ultrasoundRegions = image.ultrasoundRegions;
      if (
        Helper.Validator.isDefined(ultrasoundRegions) === true &&
        ultrasoundRegions.length > 0
      ) {
        if (
          !(ultrasoundRegions[0]["unitsX"] !== 3) ||
          !(ultrasoundRegions[0]["unitsY"] !== 3)
        ) {
          hasUnit = false;
        }
      } else {
        hasUnit = false;
      }
    }

    const lineWidth = toolStyle.getToolWidth();
    const { handleRadius, drawHandlesOnHover } = this.configuration;
    const context = getNewContext(eventData.canvas);

    // Pixel Spacing

    const ps = Helper.DICOM.getPixelSpacingByModality(modality, image);
    //const hasPixelSpacing = image && ps.x && ps.y;
    const hasPixelSpacing = image.isExistPixelSpacing;
    // const hasPixelSpacing =
    //   image && image.pixelSpacing[0] && image.pixelSpacing[1];
    //image && image.rowPixelSpacing && image.columnPixelSpacing;

    image = image || {};
    const pixelSpacing = {
      rowPixelSpacing: ps.x || 1,
      columnPixelSpacing: ps.y || 1,
    };
    // const pixelSpacing = {
    //   rowPixelSpacing: image.pixelSpacing[0] || 1,
    //   columnPixelSpacing: image.pixelSpacing[1] || 1,
    // };

    draw(context, (context) => {
      // If we have tool data for this element - iterate over each set and draw it
      for (let i = 0; i < toolData.data.length; i++) {
        const data = toolData.data[i];

        if (data.visible === false || data.willbeRemove === true) {
          continue;
        }

        // Configure
        const color = toolColors.getColorIfActive(data);
        const handleColor = toolColors.getHandleColorIfActive(data);
        const handleOptions = {
          color: handleColor,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          lineWidth: 3,
        };

        setShadow(context, this.configuration);

        // Draw
        drawEllipse(
          context,
          element,
          data.handles.start,
          data.handles.end,
          { color, lineWidth: 3 },
          "pixel",
          data.handles.initialRotation
        );

        drawHandles(context, eventData, data.handles, handleOptions);

        // Update textbox stats
        if (data.invalidated === true) {
          if (data.cachedStats) {
            _throttledUpdateCachedStats(
              image,
              element,
              data,
              modality,
              pixelSpacing
            );
          } else {
            _updateCachedStats(image, element, data, modality, pixelSpacing);
          }
        }

        // Default to textbox on right side of ROI
        if (!data.handles.textBox.hasMoved) {
          data.handles.textBox.x = Math.max(
            data.handles.start.x,
            data.handles.end.x
          );
          data.handles.textBox.y =
            (data.handles.start.y + data.handles.end.y) / 2;
        }

        const textBoxAnchorPoints = (handles) =>
          _findTextBoxAnchorPoints(handles.start, handles.end);
        const textBoxContent = _createTextBoxContent(
          context,
          image.color,
          data.cachedStats,
          modality,
          hasPixelSpacing,
          image.usePixelSpacingApproximateValue,
          image.hasPhysicalDelta,
          hasUnit,
          image,
          this.configuration
        );
        const linkColor = toolColors.getLinkColorIfActive(data);
        drawLinkedTextBoxStyle(
          context,
          element,
          data.handles.textBox,
          textBoxContent,
          data.handles,
          textBoxAnchorPoints,
          color,
          linkColor,
          lineWidth,
          0,
          true
        );
      }
    });
  }
}

/**
 *
 */
const _throttledUpdateCachedStats = throttle(_updateCachedStats, 110);

/**
 *
 *
 * @param {*} image
 * @param {*} element
 * @param {*} data
 * @param {string} modality
 * @param {*} pixelSpacing
 * @returns {void}
 */
function _updateCachedStats(image, element, data, modality, pixelSpacing) {
  const stats = _calculateStats(
    image,
    element,
    data.handles,
    modality,
    pixelSpacing
  );

  data.cachedStats = stats;
  data.invalidated = false;
}

/**
 * TODO: This is the same method (+ GetPixels) for the other ROIs
 * TODO: The pixel filtering is the unique bit
 *
 * @param {*} startHandle
 * @param {*} endHandle
 * @returns {{ left: number, top: number, width: number, height: number}}
 */
function _getEllipseImageCoordinates(startHandle, endHandle) {
  return {
    left: Math.min(startHandle.x, endHandle.x),
    top: Math.min(startHandle.y, endHandle.y),
    width: Math.abs(startHandle.x - endHandle.x),
    height: Math.abs(startHandle.y - endHandle.y),
  };
}

/**
 *
 *
 * @param {*} image
 * @param {*} element
 * @param {*} handles
 * @param {*} modality
 * @param {*} pixelSpacing
 * @returns {Object} The Stats object
 */
function _calculateStats(image, element, handles, modality, pixelSpacing) {
  // Retrieve the bounds of the rectangle in image coordinates
  const roiCoordinates = _getEllipseImageCoordinates(
    handles.start,
    handles.end
  );

  // Retrieve the array of pixels that the rectangle bounds cover
  const pixels = getPixels(
    element,
    roiCoordinates.left,
    roiCoordinates.top,
    roiCoordinates.width,
    roiCoordinates.height,
    containXY
  );

  // Calculate the mean & standard deviation from the pixels and the rectangle details
  const roiMeanStdDev = _calculateEllipseStats(pixels, roiCoordinates);

  let alpha = 1;
  // Calculate the image area from the rectangle dimensions and pixel spacing
  // ref : https://theengineeringmindset.com/area-of-an-ellipse/
  let area =
    (roiCoordinates.width / 2) *
    (pixelSpacing.columnPixelSpacing || 1) *
    ((roiCoordinates.height / 2) * (pixelSpacing.rowPixelSpacing || 1)) *
    3.14 *
    0.01;
  if (modality.toLowerCase() === "us") {
    const units = Helper.DICOM.getPhysicalDirectionUnit(image);
    if (units.x === "cm") {
      alpha = 100;
    } else {
      area = 0;
    }
  }

  area = area * alpha;

  let stats = {
    area: area || 0,
    count: roiMeanStdDev.count || 0,
    mean: roiMeanStdDev.mean || 0,
    variance: roiMeanStdDev.variance || 0,
    stdDev: roiMeanStdDev.stdDev || 0,
    min: roiMeanStdDev.min || 0,
    max: roiMeanStdDev.max || 0,
  };

  if (modality.toLowerCase() === "pt") {
    if (!!image.isSupportSUV === true) {
      stats["meanSUV"] = calculateSUV(image, roiMeanStdDev.mean, true) || 0;
      stats["stdDevSUV"] = calculateSUV(image, roiMeanStdDev.stdDev, true) || 0;
      stats["minSUV"] = calculateSUV(image, roiMeanStdDev.min, true) || 0;
      stats["maxSUV"] = calculateSUV(image, roiMeanStdDev.max, true) || 0;
    }
  }

  return stats;
}

function containXY(roiArea, x, y) {
  const ellipseStat = {
    center: {
      x: roiArea.left + roiArea.width / 2,
      y: roiArea.top + roiArea.height / 2,
    },
    width: Math.abs(roiArea.width),
    height: Math.abs(roiArea.height),
  };
  if (
    _isInEllipse(
      ellipseStat.center.x,
      ellipseStat.center.y,
      x,
      y,
      ellipseStat.width,
      ellipseStat.height
    )
  ) {
    return false;
  }
  return true;
}

/**
 * @description 현재 좌표가 Ellipse 안에 있는지 체크.
 * @ref @link https://www.geeksforgeeks.org/check-if-a-point-is-inside-outside-or-on-the-ellipse/
 * @function isInEllipse
 
 *
 * @param {*} h     x coordinate for ellipse center
 * @param {*} k     y coordinate for ellipse center
 * @param {*} x     x coordinate for check point
 * @param {*} y     y coordinate for check point
 * @param {*} a     width of ellipse
 * @param {*} b     height of ellipse
 *
 * @return  < 1 : inside ellipse
 *          == 1 : on Ellipse
 *          > 1 : outside ellipse
 */
function _isInEllipse(h, k, x, y, a, b) {
  const xRadius = a / 2;
  const yRadius = b / 2;

  if (xRadius <= 0.0 || yRadius <= 0.0) {
    return false;
  }

  const normalized = {
    x: x - h,
    y: y - k,
  };
  const value =
    (normalized.x * normalized.x) / (xRadius * xRadius) +
    (normalized.y * normalized.y) / (yRadius * yRadius);
  return value <= 1.0;
}

/**
 *
 *
 * @param {*} sp
 * @param {*} rectangle
 * @returns {{ count, number, mean: number,  variance: number,  stdDev: number,  min: number,  max: number }}
 */
function _calculateEllipseStats(sp, rectangle) {
  let sum = 0;
  let sumSquared = 0;
  let count = 0;
  let index = 0;
  let min = sp ? sp[0] : null;
  let max = sp ? sp[0] : null;

  for (let i = 0; i < sp.length; i++) {
    sum += sp[index];
    sumSquared += sp[index] * sp[index];
    min = Math.min(min, sp[index]);
    max = Math.max(max, sp[index]);
    count++; // TODO: Wouldn't this just be sp.length?
    index++;
  }

  if (count === 0) {
    return {
      count,
      mean: 0.0,
      variance: 0.0,
      stdDev: 0.0,
      min: 0.0,
      max: 0.0,
    };
  }

  const mean = sum / count;
  const variance = sumSquared / count - mean * mean;

  return {
    count,
    mean,
    variance,
    stdDev: Math.sqrt(variance),
    min,
    max,
  };
}

/**
 *
 *
 * @param {*} startHandle
 * @param {*} endHandle
 * @returns {Array.<{x: number, y: number}>}
 */
function _findTextBoxAnchorPoints(startHandle, endHandle) {
  const { left, top, width, height } = _getEllipseImageCoordinates(
    startHandle,
    endHandle
  );

  return [
    {
      // Top middle point of rectangle
      x: left + width / 2,
      y: top,
    },
    {
      // Left middle point of rectangle
      x: left,
      y: top + height / 2,
    },
    {
      // Bottom middle point of rectangle
      x: left + width / 2,
      y: top + height,
    },
    {
      // Right middle point of rectangle
      x: left + width,
      y: top + height / 2,
    },
  ];
}

/**
 *
 *
 * @param {*} area
 * @param {*} hasPixelSpacing
 * @returns {string} The formatted label for showing area
 */
function _formatArea(area, hasPixelSpacing, hasUnit) {
  // This uses Char code 178 for a superscript 2

  let suffix = "";
  if (hasUnit) {
    suffix = hasPixelSpacing
      ? //? ` mm${String.fromCharCode(178)}`
        ` cm${String.fromCharCode(178)}`
      : ` px${String.fromCharCode(178)}`;
  }

  return `  Area=${numbersWithCommas(area.toFixed(2))}${suffix}`; // for mm
  //return `Area: ${numbersWithCommas((area / 10).toFixed(2))}${suffix}`; // for cm
}

function _formatAreaUnit(hasPixelSpacing) {
  let suffix = hasPixelSpacing
    ? //? ` mm${String.fromCharCode(178)}`
      ` cm${String.fromCharCode(178)}`
    : ` px${String.fromCharCode(178)}`;

  return suffix;
}

/**
 * TODO: This is identical to EllipticalROI's same fn
 * TODO: We may want to make this a utility for ROIs with these values?
 *
 * @param {*} context
 * @param {*} isColorImage
 * @param {*} { area, mean, stdDev, min, max, meanStdDevSUV }
 * @param {*} modality
 * @param {*} hasPixelSpacing
 * @param {*} [options={}]
 * @returns {string[]}
 */
function _createTextBoxContent(
  context,
  isColorImage,
  { area, mean, stdDev, min, max, count, meanSUV, stdDevSUV, minSUV, maxSUV },
  modality,
  hasPixelSpacing,
  hasAproximatePixelSpacing,
  hasPhysicalDelta,
  hasUnit,
  image,
  options = {}
) {
  const showMinMax = options.showMinMax || false;
  const showHounsfieldUnits = options.showHounsfieldUnits !== false;
  const textLines = [];

  const otherLines = [];

  const normalTextStyle = {
    font: textStyle.getFont(),
    fontSize: textStyle.getFontSize(),
    textBaseline: "top",
    fontColor: textStyle.getFontColor(),
  };

  const defaultFontSize = textStyle.getFontSize();
  const unitTextStyle = {
    font: `${defaultFontSize}px arial Regular`,
    fontSize: textStyle.getFontSize(),
    textBaseline: "top",
    fontColor: textStyle.getFontColor(),
  };
  const showStatisticalInfo = count > 0 ? true : false;

  if (!isColorImage && showStatisticalInfo) {
    let meanString = `Mean=${numbersWithCommas(mean.toFixed(2))}`;
    const stdDevString = `SD=${numbersWithCommas(stdDev.toFixed(2))}`;

    otherLines.push(`  ${meanString} ${stdDevString}`);

    if (showMinMax) {
      const minString = `Min=${min.toFixed(2)}`;
      let maxString = `Max=${max.toFixed(2)}`;
      otherLines.push(`  ${maxString} ${minString}`);
    }
  }

  if (modality.toUpperCase() === "US") {
    if (hasUnit === true || hasPixelSpacing || hasPhysicalDelta) {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === "cm") {
        /// display area
        textLines.push([
          _formatArea(
            area,
            hasPixelSpacing | hasPhysicalDelta,
            false /* hasUnit*/
          ),
          normalTextStyle,
          false,
        ]);

        /// display area unit
        textLines.push([
          _formatAreaUnit(hasPixelSpacing | hasPhysicalDelta),
          unitTextStyle,
          false,
        ]);
        textLines.push([" (" + count + " px)", normalTextStyle, true]);
      } else {
        textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
      }
    } else {
      textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
    }
  } else {
    if (hasPixelSpacing === true || hasAproximatePixelSpacing === true) {
      /// display area
      textLines.push([
        _formatArea(
          area,
          hasPixelSpacing | hasAproximatePixelSpacing,
          false /* hasUnit*/
        ),
        normalTextStyle,
        false,
      ]);

      /// display area unit
      textLines.push([
        _formatAreaUnit(hasPixelSpacing | hasAproximatePixelSpacing),
        unitTextStyle,
        false,
      ]);

      /// display pixel count
      textLines.push([" (" + count + " px)", normalTextStyle, true]);
    } else {
      /// display pixel count
      textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
    }
  }

  otherLines
    .slice()
    .reverse()
    .forEach((x) => textLines.unshift([x, normalTextStyle, true]));

  if (modality.toLowerCase() === "pt") {
    if (!!image.isSupportSUV === true) {
      textLines.push(["", normalTextStyle, true]);
      textLines.push(["  SUVbw (g/ml)", normalTextStyle, true]);
      let meanString = `Mean=${numbersWithCommas(meanSUV.toFixed(2))}`;
      const stdDevString = `SD=${numbersWithCommas(stdDevSUV.toFixed(2))}`;
      textLines.push([
        `  ${meanString} ${stdDevString}`,
        normalTextStyle,
        true,
      ]);
      const minString = `Min=${minSUV.toFixed(2)} `;
      let maxString = `Max=${maxSUV.toFixed(2)} `;
      textLines.push([`  ${maxString} ${minString}`, normalTextStyle, true]);
    }
  }

  return textLines;
}
